import React, { useState, useEffect, useRef } from 'react';
import classNames from 'classnames';
import { select as d3select } from 'd3-selection';
import { arc as d3arc, pie as d3pie } from 'd3-shape';
import { interpolate as d3interpolate } from 'd3-interpolate';
import 'd3-transition';

import ChartBase, { formatClassName, formatTitle, humanizeNumber, useChartBase } from './chart-base';
import TweenedNumber from 'core/ui/tweened-number';

const defaultSortValues = (values) => {
  values.sort((a, b) => b[2] - a[2]);
  return values;
};

const PieChart = (props) => {
  const {
    show_legend = true,
    donutWidth = 0,
    sortValues: sortValuesProp,
    chartVariantClassName = '',
    labelWriterFn,
    customFormatClassNameFn,
  } = props;

  const base = useChartBase(props);
  const [hovering, setHovering] = useState(null);
  const hoveringRef = useRef(null);
  const svgContainerRef = useRef(null);

  // D3 instance refs - persist across renders
  const d3Refs = useRef({
    root: null,
    svg: null,
    chartGroup: null,
    arc: null,
    pie: null,
    tweenSlice: null,
    initialized: false,
  });

  // Keep hovering ref in sync for stale D3 closures
  useEffect(() => {
    hoveringRef.current = hovering;
  }, [hovering]);

  // Sync hover state to D3 .hovering class so the CSS scale(1) override
  // on .value.hovering cancels the scale(.9) applied to all .value elements,
  // preventing the outer-edge flicker loop.
  useEffect(() => {
    const { chartGroup } = d3Refs.current;
    if (!chartGroup) return;
    chartGroup.selectAll('g.value')
      .classed('hovering', (d) => d.data[0] === hovering);
  }, [hovering]);

  const sortValues = sortValuesProp || defaultSortValues;

  function getClassName() {
    const filters = props.filters || [];
    const isFiltered = filters.length;
    const isNegated = isFiltered && filters[0].is_negated;
    return classNames('pie-chart', { hovering: !!hovering }, { 'is-negated': isNegated }, chartVariantClassName);
  }

  function setPieColors(values) {
    if (!values) return;
    const colorScale = window.skin?.colors?.pieChart || [
      "#C4197E", "#4A65A5", "#d880aa", "#9898ca", "#452860",
      "#e8eeae", "#CEDC27", "#7d6889", "#F68A3B", "#fecaa1"
    ];
    let color = 0;
    values.forEach((val) => {
      if (val[2] < 1) {
        val[2] = 0.1;
        val[4] = colorScale[color % colorScale.length] + "80";
        color++;
      } else {
        val[4] = colorScale[color % colorScale.length];
        color++;
      }
    });
    return values;
  }

  function getSortedValues(config, values, filters) {
    let labels = config.value_labels;
    if (!labels || Object.keys(labels).length === 0) {
      labels = Object.fromEntries(Object.keys(values).map(val => [val, val]));
    }
    let sorted = sortValues(
      Object.entries(labels).map(([val, label]) => [
        val, label, values[val] || 0, filters.find(filter => filter.value === val)
      ])
    );
    sorted = setPieColors(sorted);
    return sorted;
  }

  function generateSortedValues() {
    const config = props.config;
    const value_series = config.id;
    const values = props.values[config.ds + '.' + value_series] || {};
    const shouldTween = props.tween;
    const filters = props.filters || [];
    const sorted_values = getSortedValues(config, values, filters);
    let chartIsNegated = 0;
    if (filters.length > 0) {
      filters.forEach(filter => {
        if (filter.is_negated) {
          chartIsNegated = 1;
        }
      });
    }
    if (chartIsNegated) {
      filters.forEach(filter => filter.is_negated = 1);
    }
    return { sorted_values, chartIsNegated, shouldTween, config, value_series };
  }

  function updateSlices() {
    const { pie, chartGroup, arc, tweenSlice } = d3Refs.current;
    if (!pie) return;

    const filters = props.filters || [];
    const config = props.config;
    const value_series = config.ds + '.' + config.id;
    const viewOnly = props.viewOnly;

    let values = props.values[value_series];
    if (!values) return;
    if (Object.keys(values).length === 0) {
      values = { 1: 0.1 };
    }

    const sorted_values = getSortedValues(config, values, filters);

    function findMinMax(arr) {
      if (arr.length === 0) return;
      let min = 0, max = 0;
      for (let i = 0; i < arr.length; i++) {
        if (arr[i][2] < min) min = arr[i][2];
        if (arr[i][2] > max) max = arr[i][2];
      }
      return max - min;
    }

    function hasValueGreaterThanOne(arrays) {
      for (let i = 0; i < arrays.length; i++) {
        if (arrays[i][2] >= 1) return true;
      }
      return false;
    }

    function replaceValuesWithZero(arrays) {
      let value_update = "0.01";
      if (viewOnly && findMinMax(arrays) > 5) {
        value_update = findMinMax(arrays) / 100 * 0.5;
      }
      for (let i = 0; i < arrays.length; i++) {
        if (arrays[i][2] === 0.1) {
          arrays[i][2] = parseFloat(value_update);
        }
      }
    }

    if (hasValueGreaterThanOne(sorted_values)) {
      replaceValuesWithZero(sorted_values);
    }

    const slice_values = pie(sorted_values);
    const sliceGroups = chartGroup.selectAll('g.value').data(slice_values);

    const onSliceClick = (d) => {
      const sv = generateSortedValues();
      base.onFilterClick(
        !d.data[3],
        sv.config,
        sv.value_series,
        hoveringRef.current,
        sv.chartIsNegated
      );
    };

    const newSliceGroups = sliceGroups.enter()
      .append('g')
      .attr('class', (d) => 'value-' + (customFormatClassNameFn ? customFormatClassNameFn(d.data[1]) : formatClassName(d.data[1])))
      .attr('fill', (d) => d.data[4])
      .classed('value', true)
      .classed('is-selected', (d) => d.data[3])
      .classed('negated-slice', (props.config.excluded ? (d) => d.data[3] : null))
      .classed('hovering', (d) => hoveringRef.current === d.data[0])
      .on('mouseover', (_, d) => setHovering(d.data[0]))
      .on('click', (_, d) => onSliceClick(d))
      .on('mouseout', () => setHovering(null));

    newSliceGroups
      .append('title')
      .text((d) => formatTitle(d.data));

    newSliceGroups
      .append('path')
      .attr('class', 'slice')
      .attr('d', arc({ startAngle: 0, endAngle: 0 }))
      .transition().duration(500).attrTween('d', tweenSlice);

    if (labelWriterFn) {
      const applyLabelWriter = (isUpdate) => (slice, i, labelGroups) =>
        labelWriterFn(d3select(labelGroups[i]), isUpdate, slice, i, d3Refs.current);
      newSliceGroups
        .append('g')
        .attr('class', 'label')
        .each(applyLabelWriter(false));
      sliceGroups.select('g.label')
        .each(applyLabelWriter(true));
    }

    sliceGroups
      .classed('is-selected', (d) => d.data[3])
      .classed('negated-slice', (props.config.excluded ? (d) => d.data[3] : null))
      .classed('hovering', (d) => hoveringRef.current === d.data[0])
      .on('mouseover', (_, d) => setHovering(d.data[0]))
      .on('click', (_, d) => onSliceClick(d))
      .on('mouseout', () => setHovering(null))
      .select('path')
      .transition().duration(500).attrTween('d', tweenSlice);

    sliceGroups
      .select('title')
      .text((d) => formatTitle(d.data));
  }

  // Initialize D3 on mount
  useEffect(() => {
    const container = svgContainerRef.current;
    if (!container || d3Refs.current.initialized) return;

    const root = d3select(container);
    const svg = root.append('svg').attr('viewBox', '0 0 100 100');
    const chartGroup = svg
      .append('g')
      .attr('class', 'center')
      .attr('transform', 'translate(50,50)');

    const outerRadius = 50;
    const arc = d3arc()
      .innerRadius(donutWidth ? outerRadius - donutWidth : 0)
      .outerRadius(outerRadius);

    const tweenSlice = function (toVal) {
      const fromVal = this._lastVal || { startAngle: 0, endAngle: 0 };
      const step = d3interpolate(fromVal, toVal);
      this._lastVal = step(1);
      return t => arc(step(t));
    };

    const pie = d3pie().value(d => d[2]).sort(null);

    d3Refs.current = {
      root, svg, chartGroup, arc, pie, tweenSlice, initialized: true
    };

    updateSlices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

// Update D3 slices when slice-related props change (mirrors componentDidUpdate)
  useEffect(() => {
    if (d3Refs.current.initialized) {
      updateSlices();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.values, props.filters, props.config, props.donutWidth]);

  function renderChart() {
    return (
      <>
        <div className='svg-root' ref={svgContainerRef} />
        {show_legend !== false && (
          <ul className='legend'>
            {generateSortedValues().sorted_values.map(([val, label, value, filter]) => {
              const isSelected = !!filter;

              const checkIfNegated = (item) => {
                if (item) {
                  if (item.is_negated === 1) return true;
                }
                return false;
              };

              return (
                <li
                  className={classNames('legend-label', `label-${formatClassName(label)}`, {
                    'is-selected': isSelected,
                    hovering: hovering === val,
                    isNegated: checkIfNegated(filter),
                    selected: isSelected,
                    not: generateSortedValues().chartIsNegated
                  })}
                  key={val}
                  onClick={() => base.onFilterClick(
                    !isSelected,
                    generateSortedValues().config,
                    generateSortedValues().value_series,
                    val,
                    generateSortedValues().chartIsNegated
                  )}
                  onMouseEnter={() => setHovering(val)}
                  onMouseLeave={() => setHovering(null)}
                >
                  <span className="row-wrapper">
                    <span className="flex">
                      <label>{label}</label>
                      <span className="count-value">
                        {generateSortedValues().shouldTween && <TweenedNumber className='count' value={value} />}
                        {!generateSortedValues().shouldTween && <span className='count'>{humanizeNumber(value)}</span>}
                      </span>
                    </span>
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </>
    );
  }

  return (
    <ChartBase
      {...props}
      base={base}
      chartClassName={getClassName()}
    >
      <div className={getClassName()}>
        {renderChart()}
      </div>
    </ChartBase>
  );
};

export default PieChart;
